import React, { useEffect, useCallback, useRef, useState } from 'react';
import Editor from '@monaco-editor/react';
import styled from 'styled-components';
import { validateOpenAPISpec } from '../utils/validator';
import CustomApiViewer from './CustomApiViewer';
import yaml from 'js-yaml';
import PromptModal from './PromptModal';

const Container = styled.div`
  display: flex;
  height: calc(100% - 72px);
  position: relative;
`;

const EditorContainer = styled.div`
  flex: 1;
  min-width: 0;
  border-right: 1px solid ${props => props.$darkMode ? '#30363d' : '#e1e4e8'};
  position: relative;
`;

const PreviewContainer = styled.div`
  flex: 1;
  min-width: 0;
  border-right: 1px solid ${props => props.$darkMode ? '#30363d' : '#e1e4e8'};
  overflow: hidden;
  position: relative;
`;

const ViewerSelector = styled.select`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  padding: 4px 8px;
  background: ${props => props.$darkMode ? '#21262d' : '#ffffff'};
  border: 1px solid ${props => props.$darkMode ? '#30363d' : '#d0d7de'};
  border-radius: 4px;
  font-size: 11px;
  color: ${props => props.$darkMode ? '#c9d1d9' : '#24292f'};
`;

const PreviewFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const Breadcrumb = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  background: ${props => props.$darkMode ? 'rgba(13, 17, 23, 0.9)' : 'rgba(255, 255, 255, 0.9)'};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  color: ${props => props.$darkMode ? '#8b949e' : '#586069'};
  z-index: 10;
  pointer-events: none;
  font-family: monospace;
`;



function SpecEditor({ spec, rules, advancedMode, darkMode, format, splitView, onSpecChange, onValidationChange, onErrorClick }) {
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [pendingComponent, setPendingComponent] = useState(null);
  const [displayContent, setDisplayContent] = useState(spec);
  const [breadcrumb, setBreadcrumb] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewViewer, setPreviewViewer] = useState('custom');
  
  useEffect(() => {
    if (onErrorClick) {
      window.jumpToLine = (line) => {
        if (editorRef.current && line) {
          editorRef.current.revealLineInCenter(line);
          editorRef.current.setPosition({ lineNumber: line, column: 1 });
          editorRef.current.focus();
        }
      };
    }
    
    window.highlightFix = (oldContent, newContent) => {
      if (!editorRef.current || !monacoRef.current) return;
      
      setTimeout(() => {
        if (!editorRef.current || !monacoRef.current) return;
        
        const oldLines = oldContent.split('\n');
        const newLines = newContent.split('\n');
        const decorations = [];
        
        for (let i = 0; i < newLines.length; i++) {
          if (i >= oldLines.length || oldLines[i] !== newLines[i]) {
            if (i >= oldLines.length || newLines[i].trim() !== '') {
              decorations.push({
                range: new monacoRef.current.Range(i + 1, 1, i + 1, 1),
                options: {
                  isWholeLine: true,
                  className: 'highlight-fix-line',
                  glyphMarginClassName: 'highlight-fix-glyph'
                }
              });
            }
          }
        }
        
        if (decorations.length === 0) return;
        
        const decorationIds = editorRef.current.deltaDecorations([], decorations);
        
        const firstLine = decorations[0].range.startLineNumber;
        editorRef.current.revealLineInCenter(firstLine);
        editorRef.current.setPosition({ lineNumber: firstLine, column: 1 });
        editorRef.current.focus();
        
        setTimeout(() => {
          if (editorRef.current) {
            editorRef.current.deltaDecorations(decorationIds, []);
          }
        }, 3000);
      }, 100);
    };
  }, [onErrorClick]);
  
  useEffect(() => {
    try {
      if (!spec || spec.trim().startsWith('<')) {
        setDisplayContent('openapi: 3.0.0\ninfo:\n  title: New API\n  version: 1.0.0\npaths: {}');
        return;
      }
      const parsed = yaml.load(spec);
      if (format === 'json') {
        setDisplayContent(JSON.stringify(parsed, null, 2));
      } else {
        setDisplayContent(spec);
      }
    } catch (e) {
      setDisplayContent(spec);
    }
  }, [spec, format]);

  const validateSpec = useCallback(async (specContent) => {
    const validationErrors = await validateOpenAPISpec(specContent, format);

    // Apply custom rules
    const ruleErrors = rules.reduce((errors, rule) => {
      if (rule.enabled && rule.validate) {
        try {
          const parsed = yaml.load(specContent);
          const ruleResult = rule.validate(parsed, specContent);
          if (ruleResult) errors.push(ruleResult);
        } catch (e) {}
      }
      return errors;
    }, []);

    const allErrors = [...validationErrors, ...ruleErrors];
    onValidationChange(allErrors);

    // Update editor markers
    if (editorRef.current && monacoRef.current) {
      const markers = allErrors
        .filter(error => error.line)
        .map(error => {
          let severity;
          if (error.type === 'error') {
            severity = monacoRef.current.MarkerSeverity.Error; // Red underline
          } else if (error.type === 'warning') {
            severity = monacoRef.current.MarkerSeverity.Warning; // Yellow underline
          } else {
            severity = monacoRef.current.MarkerSeverity.Info; // Blue underline
          }
          
          return {
            startLineNumber: error.line,
            startColumn: error.column || 1,
            endLineNumber: error.line,
            endColumn: error.column ? error.column + 10 : 100,
            message: error.message,
            severity
          };
        });

      monacoRef.current.editor.setModelMarkers(editorRef.current.getModel(), 'validation', markers);
    }
  }, [rules, onValidationChange, format]);

  useEffect(() => {
    const timer = setTimeout(async () => await validateSpec(format === 'json' ? displayContent : spec), 500);
    return () => clearTimeout(timer);
  }, [spec, displayContent, format, validateSpec]);

  const handleEditorChange = (value) => {
    try {
      if (format === 'json') {
        const parsed = JSON.parse(value || '{}');
        const yamlContent = yaml.dump(parsed, { indent: 2, noRefs: true });
        onSpecChange(yamlContent);
      } else {
        onSpecChange(value || '');
      }
    } catch (e) {
      // Invalid format, keep as is
      if (format === 'yaml') {
        onSpecChange(value || '');
      }
    }
  };

  useEffect(() => {
    if (splitView && previewViewer !== 'custom') {
      try {
        const parsed = yaml.load(spec);
        let html = '';

        if (previewViewer === 'swagger') {
          html = `<!DOCTYPE html>
<html>
<head>
  <link rel="stylesheet" type="text/css" href="https://unpkg.com/swagger-ui-dist@5.9.0/swagger-ui.css" />
</head>
<body>
  <div id="swagger-ui"></div>
  <script src="https://unpkg.com/swagger-ui-dist@5.9.0/swagger-ui-bundle.js"></script>
  <script>
    SwaggerUIBundle({
      spec: ${JSON.stringify(parsed)},
      dom_id: '#swagger-ui',
      presets: [SwaggerUIBundle.presets.apis, SwaggerUIBundle.presets.standalone]
    });
  </script>
</body>
</html>`;
        } else if (previewViewer === 'redoc') {
          html = `<!DOCTYPE html>
<html>
<head>
  <script src="https://cdn.redoc.ly/redoc/latest/bundles/redoc.standalone.js"></script>
</head>
<body>
  <div id="redoc-container"></div>
  <script>
    Redoc.init(${JSON.stringify(parsed)}, {}, document.getElementById('redoc-container'));
  </script>
</body>
</html>`;
        }
        
        setPreviewHtml(html);
      } catch (e) {
        setPreviewHtml('<html><body><p>Invalid spec</p></body></html>');
      }
    }
  }, [spec, splitView, previewViewer]);

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
    
    // Register $ref link provider
    const linkProvider = monaco.languages.registerLinkProvider(format === 'yaml' ? 'yaml' : 'json', {
      provideLinks: (model) => {
        const links = [];
        const content = model.getValue();
        const lines = content.split('\n');
        
        lines.forEach((line, lineIndex) => {
          const refMatch = line.match(/\$ref:\s*['"]?(#\/components\/schemas\/([^'"\s]+))['"]?/);
          if (refMatch) {
            const fullRef = refMatch[1];
            const schemaName = refMatch[2];
            const startCol = line.indexOf(fullRef) + 1;
            const endCol = startCol + fullRef.length;
            
            // Find the line where this schema is defined
            const targetLine = lines.findIndex(l => 
              l.match(new RegExp(`^\\s*${schemaName}:\\s*$`)) || 
              l.match(new RegExp(`['"]${schemaName}['"]:\\s*\\{`))
            );
            
            if (targetLine !== -1) {
              links.push({
                range: new monaco.Range(lineIndex + 1, startCol, lineIndex + 1, endCol),
                url: `command:editor.action.goToLine?${targetLine + 1}`,
                tooltip: `Go to ${schemaName} definition (line ${targetLine + 1})`
              });
            }
          }
        });
        
        return { links };
      }
    });
    
    // Handle link clicks
    editor.onMouseDown((e) => {
      if (e.event.ctrlKey || e.event.metaKey) {
        const position = e.target.position;
        if (position) {
          const line = editor.getModel().getLineContent(position.lineNumber);
          const refMatch = line.match(/\$ref:\s*['"]?(#\/components\/schemas\/([^'"\s]+))['"]?/);
          if (refMatch) {
            const schemaName = refMatch[2];
            const content = editor.getValue();
            const lines = content.split('\n');
            const targetLine = lines.findIndex(l => 
              l.match(new RegExp(`^\\s*${schemaName}:\\s*$`)) || 
              l.match(new RegExp(`['"]${schemaName}['"]:\\s*\\{`))
            );
            if (targetLine !== -1) {
              editor.revealLineInCenter(targetLine + 1);
              editor.setPosition({ lineNumber: targetLine + 1, column: 1 });
              editor.focus();
            }
          }
        }
      }
    });
    
    // Register OpenAPI autocomplete provider
    const completionProvider = monaco.languages.registerCompletionItemProvider(format === 'yaml' ? 'yaml' : 'json', {
      provideCompletionItems: (model, position) => {
        const textUntilPosition = model.getValueInRange({
          startLineNumber: 1,
          startColumn: 1,
          endLineNumber: position.lineNumber,
          endColumn: position.column
        });
        
        const line = model.getLineContent(position.lineNumber);
        const lineUntilPosition = line.substring(0, position.column - 1);
        const indent = lineUntilPosition.match(/^\s*/)[0].length;
        
        const suggestions = [];
        
        // Root level suggestions
        if (indent === 0 && !textUntilPosition.includes('openapi:')) {
          suggestions.push({
            label: 'openapi',
            kind: monaco.languages.CompletionItemKind.Property,
            insertText: 'openapi: 3.0.0',
            documentation: 'OpenAPI version'
          });
        }
        if (indent === 0 && !textUntilPosition.includes('info:')) {
          suggestions.push({
            label: 'info',
            kind: monaco.languages.CompletionItemKind.Property,
            insertText: format === 'yaml' ? 'info:\n  title: My API\n  version: 1.0.0' : '"info": {\n  "title": "My API",\n  "version": "1.0.0"\n}',
            documentation: 'API metadata'
          });
        }
        if (indent === 0 && !textUntilPosition.includes('paths:')) {
          suggestions.push({
            label: 'paths',
            kind: monaco.languages.CompletionItemKind.Property,
            insertText: format === 'yaml' ? 'paths:\n  /example:\n    get:\n      responses:\n        200:\n          description: Success' : '"paths": {\n  "/example": {\n    "get": {\n      "responses": {\n        "200": {\n          "description": "Success"\n        }\n      }\n    }\n  }\n}',
            documentation: 'API paths and operations'
          });
        }
        if (indent === 0 && !textUntilPosition.includes('components:')) {
          suggestions.push({
            label: 'components',
            kind: monaco.languages.CompletionItemKind.Property,
            insertText: format === 'yaml' ? 'components:\n  schemas:\n    ' : '"components": {\n  "schemas": {}\n}',
            documentation: 'Reusable components'
          });
        }
        if (indent === 0 && !textUntilPosition.includes('servers:')) {
          suggestions.push({
            label: 'servers',
            kind: monaco.languages.CompletionItemKind.Property,
            insertText: format === 'yaml' ? 'servers:\n  - url: https://api.example.com' : '"servers": [\n  {\n    "url": "https://api.example.com"\n  }\n]',
            documentation: 'API servers'
          });
        }
        
        // HTTP methods
        if (lineUntilPosition.trim() === '' && textUntilPosition.includes('paths:')) {
          ['get', 'post', 'put', 'delete', 'patch', 'options', 'head'].forEach(method => {
            suggestions.push({
              label: method,
              kind: monaco.languages.CompletionItemKind.Method,
              insertText: format === 'yaml' ? `${method}:\n  responses:\n    200:\n      description: Success` : `"${method}": {\n  "responses": {\n    "200": {\n      "description": "Success"\n    }\n  }\n}`,
              documentation: `${method.toUpperCase()} operation`
            });
          });
        }
        
        // Operation properties
        if (textUntilPosition.match(/(get|post|put|delete|patch):/)) {
          ['summary', 'description', 'operationId', 'tags', 'parameters', 'requestBody', 'responses', 'security'].forEach(prop => {
            if (!textUntilPosition.includes(prop + ':')) {
              suggestions.push({
                label: prop,
                kind: monaco.languages.CompletionItemKind.Property,
                insertText: format === 'yaml' ? `${prop}: ` : `"${prop}": `,
                documentation: `Operation ${prop}`
              });
            }
          });
        }
        
        // Schema types
        if (lineUntilPosition.includes('type:')) {
          ['string', 'number', 'integer', 'boolean', 'array', 'object'].forEach(type => {
            suggestions.push({
              label: type,
              kind: monaco.languages.CompletionItemKind.Value,
              insertText: type,
              documentation: `${type} type`
            });
          });
        }
        
        // Response codes
        if (textUntilPosition.includes('responses:') && lineUntilPosition.trim() === '') {
          ['200', '201', '204', '400', '401', '403', '404', '500'].forEach(code => {
            const descriptions = {
              '200': 'Success',
              '201': 'Created',
              '204': 'No Content',
              '400': 'Bad Request',
              '401': 'Unauthorized',
              '403': 'Forbidden',
              '404': 'Not Found',
              '500': 'Internal Server Error'
            };
            suggestions.push({
              label: code,
              kind: monaco.languages.CompletionItemKind.Constant,
              insertText: format === 'yaml' ? `${code}:\n  description: ${descriptions[code]}` : `"${code}": {\n  "description": "${descriptions[code]}"\n}`,
              documentation: descriptions[code]
            });
          });
        }
        
        // Parameter 'in' values
        if (lineUntilPosition.includes('in:')) {
          ['query', 'header', 'path', 'cookie'].forEach(loc => {
            suggestions.push({
              label: loc,
              kind: monaco.languages.CompletionItemKind.Value,
              insertText: loc,
              documentation: `Parameter in ${loc}`
            });
          });
        }
        
        // Security scheme types
        if (textUntilPosition.includes('securitySchemes:') && lineUntilPosition.includes('type:')) {
          ['apiKey', 'http', 'oauth2', 'openIdConnect'].forEach(type => {
            suggestions.push({
              label: type,
              kind: monaco.languages.CompletionItemKind.Value,
              insertText: type,
              documentation: `${type} security`
            });
          });
        }
        
        return { suggestions };
      }
    });
    
    // Cleanup on unmount
    return () => {
      completionProvider.dispose();
      linkProvider.dispose();
    };
    
    
    // Update breadcrumb on cursor position change
    editor.onDidChangeCursorPosition((e) => {
      const position = e.position;
      const model = editor.getModel();
      const lines = model.getLinesContent();
      const currentLine = lines[position.lineNumber - 1];
      
      // Build breadcrumb from indentation
      const path = [];
      const currentIndent = currentLine.search(/\S/);
      
      for (let i = position.lineNumber - 1; i >= 0; i--) {
        const line = lines[i];
        const indent = line.search(/\S/);
        const match = line.match(/^\s*([^:]+):/);
        
        if (match && indent < currentIndent) {
          path.unshift(match[1].trim());
          if (indent === 0) break;
        }
      }
      
      setBreadcrumb(path.join(' > '));
    });

    // Add keyboard shortcuts
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
      // Save/Export
      const blob = new Blob([spec], { type: 'application/x-yaml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'openapi-spec.yaml';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });

    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyF, () => {
      // Format document
      editor.getAction('editor.action.formatDocument').run();
    });
  };

  const handleAddComponent = (componentType, customValues = null) => {
    try {
      const specObject = yaml.load(spec);
      const template = customValues ? getCustomTemplate(componentType, customValues, specObject) : getTemplate(componentType, specObject);
      
      // Merge template into spec
      const newSpecObject = mergeObjects(specObject, template);
      
      const newSpec = yaml.dump(newSpecObject, { indent: 2, noRefs: true });
      onSpecChange(newSpec);
      
      // Move cursor to inserted content
      if (editorRef.current && newSpec) {
        const newSpecLines = newSpec.split('\n');
        const lastLine = newSpecLines.length - 1;
        editorRef.current.setPosition({ lineNumber: lastLine, column: newSpecLines[lastLine].length });
        editorRef.current.focus();
      }
    } catch (e) {
      console.error("Error adding component:", e);
    }
  };

  useEffect(() => {
    window.addComponent = (item) => {
      if (advancedMode && shouldPrompt(item.type)) {
        setPendingComponent(item.type);
        setShowPrompt(true);
      } else {
        handleAddComponent(item.type);
      }
    };
    return () => { delete window.addComponent; };
  }, [advancedMode, spec]);
  
  const handlePromptSubmit = (values) => {
    handleAddComponent(pendingComponent, values);
    setShowPrompt(false);
    setPendingComponent(null);
  };
  
  const handlePromptCancel = () => {
    setShowPrompt(false);
    setPendingComponent(null);
  };

  return (
    <>
      {showPrompt && (
        <PromptModal
          componentType={pendingComponent}
          onSubmit={handlePromptSubmit}
          onCancel={handlePromptCancel}
          spec={yaml.load(spec)}
        />
      )}
      <Container>
        <EditorContainer $darkMode={darkMode} $splitView={splitView}>
        {breadcrumb && <Breadcrumb $darkMode={darkMode}>{breadcrumb}</Breadcrumb>}
        <Editor
          height="100%"
          language={format === 'json' ? 'json' : 'yaml'}
          value={displayContent}
          onChange={handleEditorChange}
          onMount={handleEditorDidMount}
          theme={darkMode ? 'vs-dark' : 'light'}
          options={{
            minimap: { enabled: true, side: 'right' },
            fontSize: 14,
            wordWrap: 'on',
            scrollBeyondLastLine: false,
            folding: true,
            foldingStrategy: 'indentation'
          }}
        />
      </EditorContainer>
      {splitView && (
        <PreviewContainer $darkMode={darkMode}>
          <ViewerSelector 
            $darkMode={darkMode}
            value={previewViewer}
            onChange={(e) => setPreviewViewer(e.target.value)}
          >
            <option value="custom">Deviant Viewer</option>
            <option value="swagger">Swagger UI</option>
            <option value="redoc">Redoc</option>
          </ViewerSelector>
          {previewViewer === 'custom' ? (
            (() => {
              try {
                return <CustomApiViewer spec={yaml.load(spec)} darkMode={darkMode} />;
              } catch (e) {
                return <div style={{ padding: '20px', color: darkMode ? '#e6edf3' : '#24292f' }}>Invalid spec format</div>;
              }
            })()
          ) : (
            <PreviewFrame srcDoc={previewHtml} />
          )}
        </PreviewContainer>
      )}

    </Container>
    </>
  );
}


function mergeObjects(target, source) {
  for (const key in source) {
    if (source.hasOwnProperty(key)) {
      if (isObject(target[key]) && isObject(source[key])) {
        target[key] = mergeObjects(target[key], source[key]);
      } else {
        target[key] = source[key];
      }
    }
  }
  return target;
}

function isObject(item) {
  return (item && typeof item === 'object' && !Array.isArray(item));
}

function getTemplate(type, spec) {
  const getUniqueName = (baseName, existingKeys) => {
    let name = baseName;
    let i = 1;
    while (existingKeys.includes(name)) {
      name = baseName + i;
      i++;
    }
    return name;
  };

  const schemas = spec.components?.schemas ? Object.keys(spec.components.schemas) : [];
  const securitySchemes = spec.components?.securitySchemes ? Object.keys(spec.components.securitySchemes) : [];

  const templates = {
    // HTTP Methods
    'get-endpoint': { paths: { [getUniqueName('/resource', Object.keys(spec.paths || {}))]: { get: { summary: 'Get resource', responses: { '200': { description: 'Success' } } } } } },
    'post-endpoint': { paths: { [getUniqueName('/resource', Object.keys(spec.paths || {}))]: { post: { summary: 'Create resource', requestBody: { content: { 'application/json': { schema: { type: 'object' } } } }, responses: { '201': { description: 'Created' } } } } } },
    'put-endpoint': { paths: { [getUniqueName('/resource/{id}', Object.keys(spec.paths || {}))]: { put: { summary: 'Update resource', requestBody: { content: { 'application/json': { schema: { type: 'object' } } } }, responses: { '200': { description: 'Updated' } } } } } },
    'patch-endpoint': { paths: { [getUniqueName('/resource/{id}', Object.keys(spec.paths || {}))]: { patch: { summary: 'Patch resource', requestBody: { content: { 'application/json': { schema: { type: 'object' } } } }, responses: { '200': { description: 'Patched' } } } } } },
    'delete-endpoint': { paths: { [getUniqueName('/resource/{id}', Object.keys(spec.paths || {}))]: { delete: { summary: 'Delete resource', responses: { '204': { description: 'Deleted' } } } } } },
    'options-endpoint': { paths: { [getUniqueName('/resource', Object.keys(spec.paths || {}))]: { options: { summary: 'Options', responses: { '200': { description: 'OK' } } } } } },
    'head-endpoint': { paths: { [getUniqueName('/resource', Object.keys(spec.paths || {}))]: { head: { summary: 'Head', responses: { '200': { description: 'OK' } } } } } },
    
    // Schemas
    'string-schema': { components: { schemas: { [getUniqueName('StringSchema', schemas)]: { type: 'string' } } } },
    'number-schema': { components: { schemas: { [getUniqueName('NumberSchema', schemas)]: { type: 'number' } } } },
    'integer-schema': { components: { schemas: { [getUniqueName('IntegerSchema', schemas)]: { type: 'integer' } } } },
    'boolean-schema': { components: { schemas: { [getUniqueName('BooleanSchema', schemas)]: { type: 'boolean' } } } },
    'object-schema': { components: { schemas: { [getUniqueName('ObjectSchema', schemas)]: { type: 'object', properties: { id: { type: 'integer' } } } } } },
    'array-schema': { components: { schemas: { [getUniqueName('ArraySchema', schemas)]: { type: 'array', items: { type: 'string' } } } } },
    'enum-schema': { components: { schemas: { [getUniqueName('EnumSchema', schemas)]: { type: 'string', enum: ['value1', 'value2'] } } } },
    'allof-schema': { components: { schemas: { [getUniqueName('AllOfSchema', schemas)]: { allOf: [{ type: 'object' }] } } } },
    'oneof-schema': { components: { schemas: { [getUniqueName('OneOfSchema', schemas)]: { oneOf: [{ type: 'string' }, { type: 'number' }] } } } },
    'anyof-schema': { components: { schemas: { [getUniqueName('AnyOfSchema', schemas)]: { anyOf: [{ type: 'string' }, { type: 'number' }] } } } },
    
    // Parameters
    'path-parameter': { paths: { [getUniqueName('/resource/{id}', Object.keys(spec.paths || {}))]: { parameters: [{ name: 'id', in: 'path', required: true, schema: { type: 'string' } }] } } },
    'query-parameter': { paths: { [getUniqueName('/resource', Object.keys(spec.paths || {}))]: { get: { parameters: [{ name: 'filter', in: 'query', schema: { type: 'string' } }], responses: { '200': { description: 'Success' } } } } } },
    'header-parameter': { paths: { [getUniqueName('/resource', Object.keys(spec.paths || {}))]: { get: { parameters: [{ name: 'X-Custom-Header', in: 'header', schema: { type: 'string' } }], responses: { '200': { description: 'Success' } } } } } },
    'cookie-parameter': { paths: { [getUniqueName('/resource', Object.keys(spec.paths || {}))]: { get: { parameters: [{ name: 'session', in: 'cookie', schema: { type: 'string' } }], responses: { '200': { description: 'Success' } } } } } },
    
    // Request Bodies
    'json-request': { paths: { [getUniqueName('/resource', Object.keys(spec.paths || {}))]: { post: { requestBody: { content: { 'application/json': { schema: { type: 'object' } } } }, responses: { '201': { description: 'Created' } } } } } },
    'form-request': { paths: { [getUniqueName('/resource', Object.keys(spec.paths || {}))]: { post: { requestBody: { content: { 'application/x-www-form-urlencoded': { schema: { type: 'object', properties: { field: { type: 'string' } } } } } }, responses: { '201': { description: 'Created' } } } } } },
    'multipart-request': { paths: { [getUniqueName('/upload', Object.keys(spec.paths || {}))]: { post: { requestBody: { content: { 'multipart/form-data': { schema: { type: 'object', properties: { file: { type: 'string', format: 'binary' } } } } } }, responses: { '201': { description: 'Uploaded' } } } } } },
    'xml-request': { paths: { [getUniqueName('/resource', Object.keys(spec.paths || {}))]: { post: { requestBody: { content: { 'application/xml': { schema: { type: 'object' } } } }, responses: { '201': { description: 'Created' } } } } } },
    
    // Responses
    'success-response': { paths: { [getUniqueName('/resource', Object.keys(spec.paths || {}))]: { get: { responses: { '200': { description: 'Success', content: { 'application/json': { schema: { type: 'object' } } } } } } } } },
    'created-response': { paths: { [getUniqueName('/resource', Object.keys(spec.paths || {}))]: { post: { responses: { '201': { description: 'Created', content: { 'application/json': { schema: { type: 'object' } } } } } } } } },
    'nocontent-response': { paths: { [getUniqueName('/resource', Object.keys(spec.paths || {}))]: { delete: { responses: { '204': { description: 'No Content' } } } } } },
    'badrequest-response': { paths: { [getUniqueName('/resource', Object.keys(spec.paths || {}))]: { post: { responses: { '400': { description: 'Bad Request' } } } } } },
    'unauthorized-response': { paths: { [getUniqueName('/resource', Object.keys(spec.paths || {}))]: { get: { responses: { '401': { description: 'Unauthorized' } } } } } },
    'forbidden-response': { paths: { [getUniqueName('/resource', Object.keys(spec.paths || {}))]: { get: { responses: { '403': { description: 'Forbidden' } } } } } },
    'notfound-response': { paths: { [getUniqueName('/resource', Object.keys(spec.paths || {}))]: { get: { responses: { '404': { description: 'Not Found' } } } } } },
    'conflict-response': { paths: { [getUniqueName('/resource', Object.keys(spec.paths || {}))]: { post: { responses: { '409': { description: 'Conflict' } } } } } },
    'servererror-response': { paths: { [getUniqueName('/resource', Object.keys(spec.paths || {}))]: { get: { responses: { '500': { description: 'Internal Server Error' } } } } } },
    
    // Security
    'api-key-auth': { components: { securitySchemes: { [getUniqueName('ApiKeyAuth', securitySchemes)]: { type: 'apiKey', in: 'header', name: 'X-API-Key' } } } },
    'bearer-auth': { components: { securitySchemes: { [getUniqueName('BearerAuth', securitySchemes)]: { type: 'http', scheme: 'bearer' } } } },
    'basic-auth': { components: { securitySchemes: { [getUniqueName('BasicAuth', securitySchemes)]: { type: 'http', scheme: 'basic' } } } },
    'oauth2-auth': { components: { securitySchemes: { [getUniqueName('OAuth2', securitySchemes)]: { type: 'oauth2', flows: { authorizationCode: { authorizationUrl: 'https://example.com/oauth/authorize', tokenUrl: 'https://example.com/oauth/token', scopes: { read: 'Read access' } } } } } } },
    'openid-auth': { components: { securitySchemes: { [getUniqueName('OpenID', securitySchemes)]: { type: 'openIdConnect', openIdConnectUrl: 'https://example.com/.well-known/openid-configuration' } } } },
    
    // Configuration
    'server-config': { servers: [{ url: 'https://api.example.com', description: 'Production server' }] },
    'tag-config': { tags: [{ name: getUniqueName('tag', (spec.tags || []).map(t => t.name)), description: 'Tag description' }] },
    'external-docs': { externalDocs: { url: 'https://docs.example.com', description: 'External documentation' } },
    'contact-info': { info: { ...spec.info, contact: { name: 'API Support', email: 'support@example.com', url: 'https://example.com/support' } } },
    'license-info': { info: { ...spec.info, license: { name: 'MIT', url: 'https://opensource.org/licenses/MIT' } } },
  };
  
  return templates[type] || {};
}

function shouldPrompt(componentType) {
  return ['get-endpoint', 'post-endpoint', 'put-endpoint', 'delete-endpoint', 'patch-endpoint', 'options-endpoint', 'head-endpoint',
          'param-path', 'param-query', 'param-header', 'param-cookie',
          'response-200', 'response-201', 'response-400', 'response-401', 'response-404', 'response-500',
          'string-schema', 'object-schema', 'array-schema', 'number-schema', 'integer-schema', 'boolean-schema'].includes(componentType);
}

function getCustomTemplate(type, values, spec) {
  const getUniqueName = (baseName, existingKeys) => {
    let name = baseName;
    let i = 1;
    while (existingKeys.includes(name)) {
      name = baseName + i;
      i++;
    }
    return name;
  };

  if (type.includes('endpoint')) {
    const method = type.replace('-endpoint', '');
    const tags = values.tags ? values.tags.split(',').map(t => t.trim()).filter(t => t) : [];
    
    // Auto-add path parameters to path if they don't exist
    let path = values.path;
    if (values.parameters && values.parameters.length > 0) {
      const pathParams = values.parameters.filter(p => p.in === 'path' && p.name);
      pathParams.forEach(param => {
        if (!path.includes('{' + param.name + '}')) {
          path = path.endsWith('/') ? path + '{' + param.name + '}' : path + '/{' + param.name + '}';
        }
      });
      // Filter out empty parameter names
      values.parameters = values.parameters.filter(p => p.name);
    }
    
    const operation = {
      summary: values.summary || 'Summary',
      ...(values.description && { description: values.description }),
      ...(values.operationId && { operationId: values.operationId }),
      ...(tags.length > 0 && { tags }),
      ...(values.parameters && values.parameters.length > 0 && {
        parameters: values.parameters.map(p => ({
          name: p.name,
          in: p.in,
          required: p.in === 'path' ? true : p.required,
          schema: { type: p.type },
          ...(p.description && { description: p.description })
        }))
      }),
      ...(values.requestBody && values.requestBody.enabled && {
        requestBody: {
          required: values.requestBody.required,
          content: {
            [values.requestBody.contentType]: {
              schema: { type: values.requestBody.schemaType }
            }
          }
        }
      }),
      responses: values.responses && values.responses.length > 0 ? 
        values.responses.reduce((acc, r) => {
          acc[r.code] = {
            description: r.description || 'Response',
            ...(r.schemaType !== 'none' && {
              content: {
                'application/json': {
                  schema: r.schemaRef ? 
                    { $ref: '#/components/schemas/' + r.schemaRef } : 
                    { type: r.schemaType }
                }
              }
            })
          };
          return acc;
        }, {}) : 
        { '200': { description: 'Success' } }
    };
    
    return { paths: { [path]: { [method]: operation } } };
  }
  
  if (type.includes('schema')) {
    const schemas = spec.components?.schemas ? Object.keys(spec.components.schemas) : [];
    const schemaName = getUniqueName(values.name, schemas);
    
    const schema = {
      type: values.type,
      ...(values.description && { description: values.description }),
      ...(values.example && { example: values.example })
    };
    
    // Add properties for object schemas
    if (values.type === 'object' && values.properties && values.properties.length > 0) {
      const props = {};
      const required = [];
      
      values.properties.forEach(prop => {
        if (prop.name) {
          props[prop.name] = {
            type: prop.type,
            ...(prop.description && { description: prop.description })
          };
          if (prop.required) {
            required.push(prop.name);
          }
        }
      });
      
      schema.properties = props;
      if (required.length > 0) {
        schema.required = required;
      }
    }
    
    return { components: { schemas: { [schemaName]: schema } } };
  }
  
  return {};
}

export default SpecEditor;
